/**
 * Origins the form accepts a site header from.
 *
 * `layout.header.url` comes from the form configuration, which in the Netwalk
 * portal is free text, as `logo` already is. An iframe is not an image though:
 * without an allowlist, anyone able to edit that configuration could put
 * arbitrary content above a form where name, phone, date of birth and photos
 * are typed, wearing the look of a Netwalk page.
 *
 * The cost is a pull request here for every new tenant domain, which coincides
 * with the go-live of that website.
 */
const HEADER_ORIGIN_SUFFIXES = ['francinamgmt.com', 'wearemodels.it'];

/** Every embed route lives under this prefix. */
const HEADER_PATH_PREFIX = '/embed/';

const matchesAllowedSuffix = (hostname) =>
  HEADER_ORIGIN_SUFFIXES.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`));

// In development the tenant site runs locally, on an arbitrary port.
const isLocalhost = (hostname) => hostname === 'localhost' || hostname === '127.0.0.1';

/**
 * Validates the header URL taken from the site configuration.
 *
 * @param {string} [value] - The `layout.header.url` value
 * @returns {string|null} The URL when allowed, `null` otherwise
 */
export const resolveHeaderUrl = (value) => {
  if (!value) {
    return null;
  }

  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  const isDevelopment = process.env.NODE_ENV !== 'production';
  const isAllowedHost = matchesAllowedSuffix(url.hostname) || (isDevelopment && isLocalhost(url.hostname));
  const isAllowedProtocol = url.protocol === 'https:' || (isDevelopment && url.protocol === 'http:');

  if (!isAllowedHost || !isAllowedProtocol || !url.pathname.startsWith(HEADER_PATH_PREFIX)) {
    return null;
  }

  return url.toString();
};

/**
 * Development-only override, read from `?headerUrl=`.
 *
 * The header URL normally comes from the backend, which means the feature
 * cannot be exercised locally until that field ships. The value still goes
 * through `resolveHeaderUrl`, so the allowlist applies either way.
 *
 * @returns {string|null} The requested URL, or `null` outside development
 */
export const readHeaderUrlOverride = () => {
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  return new URLSearchParams(window.location.search).get('headerUrl');
};
